<!--
  ~ Copyright (C) 2025 - Potentially Ltd
  ~
  ~ Please see distribution for license.
  -->

<mat-card
  class="moment-card"
  [ngClass]="{ inactive: !card.visible && isFolioSection, 'moment-card--community': cardVariant === 'community' }"
>
  <!-- Community card header -->
  <div class="community-card__header" *ngIf="cardVariant === 'community'">
    <div class="community-card__author-row">
      <ptl-profile-image [userSummary]="authorProfile || (userDetailsData$ | async)"></ptl-profile-image>
      <div class="community-card__author">
        <span class="community-card__name"> {{ card.author?.fullName || (userDetailsData$ | async)?.firstName }} </span>
        <span class="community-card__subtitle" *ngIf="card.author?.role || card.playlistName">
          <ng-container *ngIf="card.author?.role">{{ card.author.role | titlecase }}</ng-container>
          <ng-container *ngIf="card.playlistName">
            <ng-container *ngIf="card.author?.role"> &middot; </ng-container>
            {{ 'translations.momentsList.attribution.in' | transloco }}
            <button
              type="button"
              class="community-card__playlist-link"
              [disabled]="loadingPlaylistLink"
              [attr.aria-label]="'translations.global.ariaLabels.viewPlaylist' | transloco: { name: card.playlistName }"
              (click)="onPlaylistLinkClick($event)"
            >
              {{ card.playlistName }}
            </button>
          </ng-container>
        </span>
      </div>
    </div>
    <div class="community-card__header-right">
      <span
        *ngIf="card.linkedLearningExperienceUid"
        class="community-card__evidence-chip"
        [attr.aria-label]="'translations.global.ariaLabels.viewInEvidence' | transloco: { name: card?.title }"
      >
        <ptl-icon [icon]="LucidePaperclip" size="xs"></ptl-icon>
        {{ 'translations.header.title.evidence' | transloco }}
      </span>
      <span class="community-card__date">{{ card.createdAt | folioCardTimeAgo }}</span>
    </div>
  </div>

  <!-- Default folio card header -->
  <div class="moment-card__top" *ngIf="cardVariant !== 'community'">
    <div class="moment-card__top__icon-container-wrapper">
      <div class="moment-card__top-left-section">
        <ptl-profile-image [userSummary]="userDetailsData$ | async"></ptl-profile-image>
        <div class="moment-card__info">
          <div class="moment-card__profile-title">
            {{ (userDetailsData$ | async)?.firstName }}
          </div>
          <div class="moment-card__date">
            {{ card.createdAt | folioCardTimeAgo }}
          </div>
        </div>
      </div>
      <button
        *ngIf="card.viewType === 'COLLECTOR'"
        mat-icon-button
        class="moment-card__button"
        [title]="selected ? 'Remove' : 'Add'"
        [class.is_selected]="selected"
        (click)="onButtonClick(); $event.stopPropagation()"
      >
        <svg class="svg" role="img" [attr.aria-label]="'translations.global.ariaLabels.icons.plus' | transloco">
          <use ogSvgUnify="assets/sprite/sprite.svg#plus"></use>
        </svg>
      </button>

      <button
        *ngIf="isFolioSection && editMode"
        [attr.aria-label]="
          !card.visible
            ? ('translations.global.ariaLabels.changeItemVisibilityToPublic' | transloco: { name: card?.title })
            : ('translations.global.ariaLabels.changeItemVisibilityToPrivate' | transloco: { name: card?.title })
        "
        (click)="onItemVisibilityState(card, $event)"
        mat-icon-button
        class="visibility-icon"
      >
        <svg class="svg" role="img" *ngIf="!card.visible" [attr.aria-label]="'translations.global.ariaLabels.icons.openedEye' | transloco">
          <use ogSvgUnify="assets/sprite/sprite.svg#visibility-off"></use>
        </svg>
        <svg class="svg" role="img" *ngIf="card.visible" [attr.aria-label]="'translations.global.ariaLabels.icons.closedEye' | transloco">
          <use ogSvgUnify="assets/sprite/sprite.svg#visibility"></use>
        </svg>
      </button>
    </div>
  </div>

  <!-- Shared content -->
  <div class="moment-card__content">
    <ng-container *ngIf="!isEditingPost; else postEditTemplate">
      <ptl-resource-preview
        class="thin-preview"
        [previewContent]="previewContent"
        [withTruncateLines]="true"
        [collage]="true"
      ></ptl-resource-preview>

      <ptl-v3-folio-masonry-card-tags [tags]="card?.tags"></ptl-v3-folio-masonry-card-tags>
    </ng-container>

    <ng-template #postEditTemplate>
      <div class="post-edit" (click)="$event.stopPropagation()" (keydown)="$event.stopPropagation()">
        <textarea class="post-edit__textarea" rows="4" [formControl]="postEditControl"></textarea>
        <div class="post-edit__actions">
          <button type="button" class="post-edit__cancel-btn" [disabled]="isSavingPostEdit" (click)="onCancelEditPost($event)">
            {{ 'translations.global.button.cancel' | transloco }}
          </button>
          <button
            type="button"
            class="post-edit__save-btn"
            [disabled]="isSavingPostEdit || !postEditControl.value?.trim()"
            (click)="onSaveEditPost($event)"
          >
            {{ 'translations.global.button.save' | transloco }}
          </button>
        </div>
      </div>
    </ng-template>
  </div>

  <!-- Community action buttons -->
  <div class="community-card__actions" *ngIf="cardVariant === 'community'">
    <button
      class="community-card__action-btn"
      type="button"
      [attr.aria-label]="'translations.global.ariaLabels.commentItem' | transloco: { name: card?.title }"
      (click)="toggleComments($event)"
    >
      <ptl-icon [icon]="LucideMessageSquare" size="s"></ptl-icon>
      <span *ngIf="card?.commentsCount">{{ card.commentsCount }}</span>
    </button>

    <button
      class="community-card__action-btn"
      type="button"
      [class.community-card__action-btn--liked]="card?.likes?.liked"
      [attr.aria-label]="'translations.global.ariaLabels.likeItem' | transloco: { name: card?.title }"
      (click)="onLikeMoment($event)"
    >
      <ptl-icon [icon]="LucideHeart" size="s"></ptl-icon>
      <span *ngIf="card?.likes?.totalLikes">{{ card.likes.totalLikes }}</span>
    </button>

    <button
      class="community-card__action-btn"
      type="button"
      [class.community-card__action-btn--saved]="card?.saved"
      [attr.aria-label]="'translations.global.ariaLabels.saveItem' | transloco: { name: card?.title }"
      [attr.aria-pressed]="card?.saved"
      (click)="onSaveMoment($event)"
    >
      <ptl-icon [icon]="LucideBookmark" size="s"></ptl-icon>
    </button>

    <div class="post-menu-wrap" *ngIf="isCurrentUserAuthor" (click)="$event.stopPropagation()" (keydown)="$event.stopPropagation()">
      <button
        class="post-menu-btn"
        type="button"
        (click)="toggleMenu($event)"
        [attr.aria-expanded]="isMenuOpen"
        [attr.aria-label]="'translations.global.ariaLabels.moreActions' | transloco"
      >
        <ptl-icon [icon]="LucideMoreHorizontal" size="s"></ptl-icon>
      </button>
      <div class="post-dropdown" *ngIf="isMenuOpen" ogClickOutside (clickOutside)="closeMenu()">
        <!-- TODO: hidden temporarily until edit view supports TipTap; re-enable once available
        <button class="post-dropdown__item" type="button" (click)="onEditPostClick($event); closeMenu()">
          <ptl-icon [icon]="LucidePencil" size="xs"></ptl-icon>
          {{ 'translations.global.button.edit' | transloco }}
        </button>
        <div class="post-dropdown__divider"></div>
        -->
        <button class="post-dropdown__item post-dropdown__item--danger" type="button" (click)="onDeletePostClick($event); closeMenu()">
          <ptl-icon [icon]="LucideTrash" size="xs"></ptl-icon>
          {{ 'translations.global.button.delete' | transloco }}
        </button>
      </div>
    </div>
  </div>

  <!-- Default folio action buttons -->
  <div class="moment-card-action-buttons" *ngIf="cardVariant !== 'community'">
    <button
      tabindex="0"
      mat-icon-button
      class="moment-action-btn"
      [attr.aria-label]="'translations.global.ariaLabels.commentItem' | transloco: { name: card?.title }"
      (click)="toggleComments($event)"
    >
      <div class="content">
        <svg class="svg" aria-hidden="true">
          <use ogSvgUnify="assets/sprite/sprite.svg#comment"></use>
        </svg>
        <span class="count">{{ card?.commentsCount || 0 }}</span>
      </div>
    </button>
    <button
      tabindex="0"
      mat-icon-button
      class="moment-action-btn"
      [attr.aria-label]="'translations.global.ariaLabels.likeItem' | transloco: { name: card?.title }"
      (click)="onLikeMoment($event)"
    >
      <div class="content">
        <ptl-icon [icon]="LucideHeart" size="s" [class.liked]="card?.likes?.liked" aria-hidden="true"></ptl-icon>
        <span class="count">{{ card?.likes?.totalLikes || 0 }}</span>
      </div>
    </button>
  </div>

  <div
    class="inline-comments"
    [class.inline-comments--open]="commentsOpen"
    (click)="$event.stopPropagation()"
    (keydown)="$event.stopPropagation()"
  >
    <ng-container #commentsContainer></ng-container>
  </div>
</mat-card>
